import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  Expand,
  List,
  Maximize2,
  PanelRight,
  Plus,
  Shrink,
  X,
} from "lucide-react";
import { useAronConversationInfo } from "../api/aronApi";
import { useAronUiStore } from "../store/aronUiStore";
import { useAronWorkbenchStore } from "../store/aronWorkbenchStore";
import { AronConversationList } from "./AronConversationList";
import { AronWordmark } from "./AronMark";
import { AttentionMark } from "./workbench/AronTabBar";
import { AronTabPane } from "./workbench/AronTabPane";

const btn =
  "inline-flex h-6 w-6 shrink-0 items-center justify-center text-gray-500 transition-colors hover:bg-gray-200 hover:text-red-600 dark:text-gray-400 dark:hover:bg-gray-700";

/** Jedna karta w pasku pływaka — tytuł, stan tury, sygnał i licznik zapisów. */
function FloatTab({
  tab,
  active,
}: {
  tab: { key: string; convId: string | null };
  active: boolean;
}) {
  const conv = useAronConversationInfo(tab.convId);
  const activate = useAronUiStore((s) => s.activateFloatTab);
  const close = useAronUiStore((s) => s.closeFloatTab);
  // Sygnalizacja („ARON pyta / czeka na zatwierdzenie") jest wspólna dla całej
  // aplikacji, więc karta pływaka miga tak samo jak karta na pełnej stronie.
  const attention = useAronWorkbenchStore((s) =>
    tab.convId ? s.attention[tab.convId] : undefined,
  );
  const title = tab.convId ? conv?.title || "Rozmowa" : "Nowa rozmowa";
  const pending = conv?.pending_writes_count ?? 0;
  const yolo = !!conv?.autopilot;

  return (
    <div
      title={title}
      className={`group/ftab flex max-w-[12rem] shrink-0 items-center gap-1 border-r border-gray-200 px-1.5 py-1 text-[11px] dark:border-gray-700 ${
        active
          ? `border-t-2 bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100 ${
              yolo ? "border-t-black dark:border-t-white" : "border-t-red-600"
            }`
          : "border-t-2 border-t-transparent bg-gray-100 text-gray-500 hover:bg-gray-200/70 dark:bg-gray-950 dark:hover:bg-gray-800"
      }`}
    >
      <button
        type="button"
        onClick={() => activate(tab.key)}
        className="flex min-w-0 flex-1 items-center gap-1 text-left"
      >
        {conv?.is_generating && (
          <span className="inline-block h-2.5 w-2.5 shrink-0 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
        )}
        <span className="min-w-0 flex-1 truncate">{title}</span>
        {attention && <AttentionMark kind={attention} />}
      </button>
      {pending > 0 && (
        <span
          title={`${pending} propozycji zapisu czeka na decyzję`}
          className="shrink-0 bg-red-600 px-1 font-mono text-[9px] font-semibold text-white"
        >
          {pending}
        </span>
      )}
      <button
        type="button"
        onClick={() => close(tab.key)}
        title="Zamknij kartę"
        className={`shrink-0 text-gray-400 hover:text-red-600 ${
          active ? "" : "opacity-0 group-hover/ftab:opacity-100"
        }`}
      >
        <X size={11} />
      </button>
    </div>
  );
}

/**
 * Zawartość pływającego okna: belka (uchwyt do przeciągania + sterowanie oknem),
 * pasek kart i panele rozmów. Karty są własne — pływak pracuje na kilku wątkach
 * naraz, tak samo jak pełna strona, a panel przy stronie zostaje jednowątkowy.
 *
 * Wszystkie karty są ZAMONTOWANE (nieaktywne tylko schowane), więc tura puszczona
 * w jednej leci dalej, kiedy patrzysz na inną.
 */
export function AronFloatWindow({
  onHeaderMouseDown,
}: {
  onHeaderMouseDown?: (e: React.MouseEvent) => void;
}) {
  const tabs = useAronUiStore((s) => s.floatTabs);
  const activeKey = useAronUiStore((s) => s.floatActiveKey);
  const openTab = useAronUiStore((s) => s.openFloatTab);
  const adopt = useAronUiStore((s) => s.adoptFloatTab);
  const minimized = useAronUiStore((s) => s.floatMinimized);
  const setMinimized = useAronUiStore((s) => s.setFloatMinimized);
  const setDockMode = useAronUiStore((s) => s.setDockMode);
  const closeDock = useAronUiStore((s) => s.closeDock);
  const dockConvId = useAronUiStore((s) => s.dockConvId);
  const restoreRect = useAronUiStore((s) => s.floatRestoreRect);
  const toggleMaximized = useAronUiStore((s) => s.toggleFloatMaximized);
  const [showList, setShowList] = useState(false);

  // Pływak bez ani jednej karty to pusta ramka — trafia się np. komuś, kto miał
  // zapisany tryb pływający sprzed kart. Zawsze zaczynamy od rozmowy z panelu
  // bocznego (albo od pustej karty roboczej).
  useEffect(() => {
    if (tabs.length === 0) openTab(dockConvId ?? null);
  }, [tabs.length, dockConvId, openTab]);

  const active = tabs.find((t) => t.key === activeKey) ?? tabs[0];
  const activeConv = useAronConversationInfo(active?.convId ?? null);
  const headerTitle = active?.convId
    ? activeConv?.title || "Rozmowa"
    : "Nowa rozmowa";

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header
        onMouseDown={onHeaderMouseDown}
        // Dwuklik w belkę (powrót do domyślnego rozmiaru) obsługuje host razem
        // z przeciąganiem — patrz AronFloatHost.
        title="Przeciągnij, aby przenieść · dwuklik przywraca domyślny rozmiar"
        className="flex shrink-0 cursor-grab items-center gap-1 border-b border-gray-200 bg-gray-100 px-1.5 py-1 active:cursor-grabbing dark:border-gray-700 dark:bg-gray-800"
      >
        <button
          type="button"
          onClick={() => setShowList((v) => !v)}
          title={showList ? "Wróć do rozmowy" : "Lista rozmów"}
          aria-pressed={showList}
          className={btn}
        >
          <List size={14} />
        </button>
        <span className="inline-flex h-5 w-8 shrink-0 items-center justify-center bg-red-600 text-white">
          <AronWordmark />
        </span>
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-700 select-none dark:text-gray-200">
          {showList ? "Rozmowy ARON" : headerTitle}
        </span>
        <button
          type="button"
          onClick={() => setMinimized(!minimized)}
          title={minimized ? "Rozwiń okno" : "Zwiń do belki"}
          className={btn}
        >
          {minimized ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        <button
          type="button"
          onClick={() => {
            setMinimized(false);
            toggleMaximized({
              x: 0,
              y: 0,
              w: window.innerWidth,
              h: window.innerHeight,
            });
          }}
          title={
            restoreRect
              ? "Przywróć poprzedni rozmiar okna"
              : "Rozciągnij okno na cały ekran"
          }
          className={btn}
        >
          {restoreRect ? <Shrink size={14} /> : <Expand size={14} />}
        </button>
        <button
          type="button"
          onClick={() => {
            setMinimized(false);
            setDockMode("side");
          }}
          title="Przypnij do boku — wróć do panelu przy stronie"
          className={btn}
        >
          <PanelRight size={14} />
        </button>
        <Link
          to={active?.convId ? `/aron?c=${active.convId}` : "/aron"}
          onClick={closeDock}
          title="Otwórz na pełnej stronie"
          className={btn}
        >
          <Maximize2 size={13} />
        </Link>
        <button type="button" onClick={closeDock} title="Zamknij" className={btn}>
          <X size={15} />
        </button>
      </header>

      {!minimized && (
        <>
          {/* Pasek kart — jak na pełnej stronie, tylko ciaśniejszy. */}
          <div className="flex shrink-0 items-stretch border-b border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-950">
            <div className="flex min-w-0 flex-1 items-stretch overflow-x-auto">
              {tabs.map((t) => (
                <FloatTab key={t.key} tab={t} active={t.key === activeKey} />
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                setShowList(false);
                openTab(null);
              }}
              title="Nowa rozmowa w tym oknie"
              className={btn}
            >
              <Plus size={14} />
            </button>
          </div>

          <div className="relative min-h-0 flex-1">
            {tabs.map((t) => (
              <div
                key={t.key}
                className={
                  t.key === activeKey && !showList
                    ? "flex h-full min-h-0 flex-col"
                    : "hidden"
                }
              >
                <AronTabPane
                  convId={t.convId}
                  active={t.key === activeKey && !showList}
                  onCreated={(convId) => adopt(t.key, convId)}
                  showDetach={false}
                />
              </div>
            ))}
            {showList && (
              <div className="h-full min-h-0">
                <AronConversationList
                  selectedId={active?.convId ?? null}
                  onSelect={(id) => {
                    openTab(id);
                    setShowList(false);
                  }}
                  onNew={() => {
                    openTab(null);
                    setShowList(false);
                  }}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
